import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useTabsStore, type AdminTab } from "@/stores/react/tabsStore";
import { useUserStore, type ReactPermissionRoute } from "@/stores/react/authStore";
import { Layout } from "@/views/layouts/Layout";
import { Main } from "@/views/layouts/main/Main";
import { Menu } from "@/views/layouts/menu/Menu";
import { Tabs } from "@/views/layouts/tabs/Tabs";

const getRouteTitle = (route?: ReactPermissionRoute) => {
    if (!route) {
        return undefined;
    }
    const title = route.meta?.title;
    return typeof title === "string" ? title : undefined;
};

const findRouteByPath = (routes: ReactPermissionRoute[], pathname: string): ReactPermissionRoute | undefined => {
    for (const route of routes) {
        if (route.path === pathname) {
            return route;
        }
        if (route.children?.length) {
            const child = findRouteByPath(route.children, pathname);
            if (child) {
                return child;
            }
        }
    }
    return undefined;
};

export interface AdminLayoutProps {
    menus?: ReactPermissionRoute[];
}

/** 管理后台基础布局，提供菜单、标签栏和页面 Outlet 的稳定承载层。 */
export function AdminLayout({ menus }: AdminLayoutProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const tabs = useTabsStore(state => state.tabs);
    const addTab = useTabsStore(state => state.addTab);
    const removeTab = useTabsStore(state => state.removeTab);
    const setActivePath = useTabsStore(state => state.setActivePath);
    const activePath = useTabsStore(state => state.activePath);
    const menuList = useUserStore(state => state.menuList);
    const visibleMenus = menus ?? menuList;
    const [asideHidden, setAsideHidden] = useState(false);
    // MemoryRouter 等非 data router 场景没有 useMatches，上层菜单树仍可提供当前页面标题。
    const matchedRouteTitle = getRouteTitle(findRouteByPath(visibleMenus, location.pathname));

    useEffect(() => {
        const menu = findRouteByPath(visibleMenus, location.pathname);
        const tab: AdminTab = {
            path: location.pathname,
            name: location.pathname.replaceAll("/", "-") || "root",
            // 隐藏详情路由不一定存在于可见菜单树，优先使用当前 Router match 的页面标题。
            title:
                matchedRouteTitle ||
                getRouteTitle(menu || (visibleMenus[0] as ReactPermissionRoute)) ||
                document.title ||
                "管理后台",
            closable: location.pathname !== "/dashboard",
            isKeepAlive: true
        };
        addTab(tab);
    }, [addTab, location.pathname, matchedRouteTitle, visibleMenus]);

    const closeTab = (path: string) => {
        const nextPath = removeTab(path);
        if (path === location.pathname && nextPath) {
            navigate(nextPath);
        }
    };

    const selectTab = (path: string) => {
        setActivePath(path);
        void navigate(path);
    };

    const closeTabsOnSide = (side: "left" | "right", path = activePath) => {
        useTabsStore.getState().closeTabsOnSide(path, side);
    };

    const closeOtherTabs = (path = activePath) => {
        useTabsStore.getState().closeMultipleTabs(path);
    };

    const closeAllTabs = () => {
        useTabsStore.getState().closeAllTabs();
        void navigate("/dashboard");
    };

    return (
        <Layout>
            <div className="admin-layout" data-admin-layout="true">
                <div className="admin-layout__body">
                    <Menu menus={visibleMenus} hidden={asideHidden} />
                    <section className="admin-layout__content">
                        <Tabs
                            tabs={tabs}
                            activePath={activePath}
                            asideHidden={asideHidden}
                            onSelect={selectTab}
                            onClose={closeTab}
                            onCloseLeft={path => closeTabsOnSide("left", path)}
                            onCloseRight={path => closeTabsOnSide("right", path)}
                            onCloseOthers={closeOtherTabs}
                            onCloseAll={closeAllTabs}
                            onToggleAside={setAsideHidden}
                        />
                        <Main title={matchedRouteTitle}>
                            <Outlet />
                        </Main>
                    </section>
                </div>
            </div>
        </Layout>
    );
}
